export const WORK_ITEM_TYPES = ['task', 'incident', 'problem', 'service_request', 'initiative'] as const;
export type WorkItemType = (typeof WORK_ITEM_TYPES)[number];

export type WorkItemSubtypeDefinition = {
  key: string;
  name: string;
};

export const DEFAULT_WORK_ITEM_SUBTYPES: WorkItemSubtypeDefinition[] = [
  { key: 'bug', name: 'Bug' },
  { key: 'new_feature', name: 'New feature' },
];
export type WorkItemSubtype = string;

export function isWorkItemType(value: string): value is WorkItemType {
  return (WORK_ITEM_TYPES as readonly string[]).includes(value);
}

export function isWorkItemSubtype(value: string): value is WorkItemSubtype {
  return /^[a-z0-9][a-z0-9_]{0,31}$/.test(value);
}

export function workItemTypeLabel(type: string): string {
  if (type === 'incident') return 'Incident';
  if (type === 'problem') return 'Problem';
  if (type === 'service_request') return 'Service Request';
  if (type === 'initiative') return 'Initiative';
  return 'Task';
}

export function workItemSubtypeLabel(
  subtype: string | null | undefined,
  definitions: readonly WorkItemSubtypeDefinition[] = DEFAULT_WORK_ITEM_SUBTYPES,
): string {
  if (!subtype) return 'None';
  const configured = definitions.find((definition) => definition.key === subtype);
  if (configured) return configured.name;
  const words = subtype.replaceAll('_', ' ');
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
}

export type WorkItemSlim = {
  id: string;
  project_id: string;
  key: string;
  sequence: number;
  title: string;
  state_id: string;
  priority: string;
  assignee_ids: string[];
  label_ids: string[];
  /** Org-wide tags, for filtering across streams. */
  tag_ids?: string[];
  parent_id: string | null;
  release_id?: string | null;
  initiative_id?: string | null;
  /** When someone in the step's stream marked this launch step live. */
  live_at?: string | null;
  live_by_id?: string | null;
  blocked_at?: string | null;
  blocked_reason?: string | null;
  blocked_by_id?: string | null;
  blocked_by_ids?: string[];
  blocked_marked_by_id?: string | null;
  sort_order: number;
  version: number;
  start_date: string | null;
  due_date: string | null;
  completed_at: string | null;
  archived_at: string | null;
  type: string;
  subtype?: string | null;
  estimate_hours?: number | null;
  created_at: string;
  updated_at: string;
  created_by_id: string | null;
};

export type InitiativeRef = {
  id: string;
  key: string;
  title: string;
  project_id: string;
  project_identifier: string;
  project_name: string;
  state: { name: string; color: string; group: string } | null;
};

/** Cancelled work is excluded from total. */
export type ProgressCounts = {
  total: number;
  done: number;
  started: number;
  cancelled: number;
};

export type ChildRollup = ProgressCounts & {
  by_subtype: Array<{ key: string; total: number; open: number }>;
};

export type DepartmentRollup = ProgressCounts & {
  project_id: string;
  project_identifier: string;
  project_name: string;
};

export type StreamRollup = ProgressCounts & { departments: DepartmentRollup[] };

/** Extra card data the list endpoints attach. */
export type WorkItemListDecorations = {
  initiative?: InitiativeRef | null;
  rollup?: ChildRollup | null;
  initiative_rollup?: StreamRollup;
  /** Plain-text start of the description, on initiatives only. */
  summary?: string;
  /** On business requirements: the notes hold more than the template's prompt. */
  has_notes?: boolean;
};

export type ReleaseRef = {
  id: string;
  name: string;
  tag: string | null;
  branch_name: string | null;
  status: string;
};

export type WorkItemRef = {
  id: string;
  key: string;
  title: string;
  type: string;
  subtype?: string | null;
  project_id: string;
  project_identifier: string;
  priority: string;
  archived_at: string | null;
  blocked_reason?: string | null;
  blocked_by_id?: string | null;
  blocked_by_ids?: string[];
  state: {
    id: string;
    name: string;
    color: string;
    group: string;
  };
  assignees: Array<{
    user_id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    avatar_url: string | null;
  }>;
};

export type WorkItemFull = WorkItemSlim & {
  description: string;
  external_source: string | null;
  external_id: string | null;
  child_ids: string[];
  parent?: WorkItemRef | null;
  children?: WorkItemRef[];
  mention_ids: string[];
  release?: ReleaseRef | null;
  initiative?: InitiativeRef | null;
  rollup?: ChildRollup | null;
  initiative_rollup?: StreamRollup;
  /** Set when read by key; false means the viewer can see this launch step but not change it. */
  viewer_is_member?: boolean;
};

export type StateDto = {
  id: string;
  name: string;
  group: string;
  color: string;
  position?: number;
  is_default: boolean;
  service_request_stage?: string | null;
};

export type LabelDto = {
  id: string;
  name: string;
  color: string;
};

export type MemberDto = {
  user_id: string;
  role: string;
  is_active: boolean;
  email?: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string | null;
};

/** Name for mention chips and pickers. Email local-part if the user has no name. */
export function memberDisplayName(member: MemberDto): string {
  const name = [member.first_name, member.last_name]
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .join(' ')
    .trim();
  if (name) return name;
  if (typeof member.email === 'string' && member.email.length > 0) {
    const local = member.email.split('@')[0];
    return local && local.length > 0 ? local : member.email;
  }
  return member.user_id;
}

/** First name for dense tables. Falls back to the full display name. */
export function memberGivenName(member: MemberDto): string {
  if (typeof member.first_name === 'string') {
    const first = member.first_name.trim();
    if (first.length > 0) return first;
  }
  return memberDisplayName(member);
}

export type ProjectDto = {
  id: string;
  name: string;
  identifier: string;
  description?: string;
  default_state_id?: string | null;
  subtypes?: WorkItemSubtypeDefinition[];
  archived_at?: string | null;
  states?: StateDto[];
  is_member?: boolean;
  requested?: boolean;
};

export type ListResponse<T> = {
  items: T[];
  page: { limit: number; offset: number; total: number };
};

export const STATE_GROUPS = [
  'backlog',
  'unstarted',
  'started',
  'completed',
  'cancelled',
] as const;

export type StateGroup = (typeof STATE_GROUPS)[number];

/** Workspace board columns — one per state group, matching seeded project names. */
export const STATE_GROUP_BOARD_COLUMNS: ReadonlyArray<{
  id: StateGroup;
  name: string;
  color: string;
}> = [
  { id: 'backlog', name: 'Backlog', color: '#60646C' },
  { id: 'unstarted', name: 'Todo', color: '#60646C' },
  { id: 'started', name: 'In Progress', color: '#F59E0B' },
  { id: 'completed', name: 'Done', color: '#46A758' },
  { id: 'cancelled', name: 'Cancelled', color: '#9AA4BC' },
];

export const COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

export function isStateGroup(value: string): value is StateGroup {
  return (STATE_GROUPS as readonly string[]).includes(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isStateDto(value: unknown): value is StateDto {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.group === 'string' &&
    typeof value.color === 'string' &&
    typeof value.is_default === 'boolean'
  );
}

export function isLabelDto(value: unknown): value is LabelDto {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.color === 'string'
  );
}

export function isProjectDto(value: unknown): value is ProjectDto {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.identifier === 'string'
  );
}

export function isMemberDto(value: unknown): value is MemberDto {
  return (
    isRecord(value) &&
    typeof value.user_id === 'string' &&
    typeof value.role === 'string' &&
    typeof value.is_active === 'boolean'
  );
}

/** Unwraps a `{ items: [...] }` envelope, rejecting any element that fails `guard`. */
export function parseItems<T>(
  value: unknown,
  guard: (item: unknown) => item is T,
  context: string,
): T[] {
  if (!isRecord(value) || !Array.isArray(value.items)) {
    throw new Error(`Malformed ${context} response.`);
  }
  if (!value.items.every(guard)) {
    throw new Error(`Malformed ${context} entry in response.`);
  }
  return value.items as T[];
}

export const UUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const WORK_ITEM_KEY_RE = /^[A-Za-z][A-Za-z0-9]{0,11}-[0-9]+$/;

/** Invalid peek values are ignored (E68). */
export function parsePeekValue(raw: string | null): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (UUID_RE.test(trimmed) || WORK_ITEM_KEY_RE.test(trimmed)) return trimmed;
  return null;
}

export function isEditorTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (target as HTMLInputElement).type;
    return type !== 'checkbox' && type !== 'radio' && type !== 'button' && type !== 'submit';
  }
  return Boolean(target.closest('[data-editor="true"]'));
}

export const WORK_ITEM_SHORTCUTS: Record<string, string> = {
  s: 'state',
  p: 'priority',
  a: 'assignees',
  i: 'title',
  l: 'labels',
};

/** Focus (and open native pickers) for contextual S/P/A/I/L. */
export function activateWorkItemShortcut(control: string): boolean {
  const el = document.querySelector<HTMLElement>(`[data-shortcut="${control}"]`);
  if (!el) return false;
  el.focus();
  if (el instanceof HTMLButtonElement || el.getAttribute('role') === 'combobox') {
    el.click();
    return true;
  }
  if (el instanceof HTMLSelectElement) {
    const picker = (el as HTMLSelectElement & { showPicker?: () => void }).showPicker;
    if (typeof picker === 'function') {
      try {
        picker.call(el);
      } catch {
        // showPicker rejects when the browser does not treat this as a user gesture.
      }
    }
  }
  return true;
}
